import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import {
  Printer, Receipt, Tag, ImageSquare, UploadSimple, Trash, CheckCircle, FloppyDisk, ArrowsClockwise, CircleNotch, Warning,
} from "@phosphor-icons/react";

/**
 * Templates tab — per-store customisation for Thermal receipt, A4 Invoice, and Dymo Label.
 * Live preview on the right while editing on the left.
 */
export default function TemplatesTab() {
  const { currentStore, stores } = useStore();
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState("thermal"); // thermal | invoice | label
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const favRef = useRef(null);
  const [uploadingFav, setUploadingFav] = useState(false);

  useEffect(() => {
    if (!currentStore?.id) return;
    setData(null);
    setDirty(false);
    api.get(`/stores/${currentStore.id}/templates`).then((r) => setData(r.data));
  }, [currentStore?.id]);

  const patchSection = (key, updates) => {
    setData((d) => ({ ...d, [key]: { ...d[key], ...updates } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put(`/stores/${currentStore.id}/templates`, {
        thermal: data.thermal,
        invoice: data.invoice,
        label: data.label,
      });
      setData(saved);
      setDirty(false);
      toast.success("Templates saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 500_000) return toast.error("Logo must be under 500 KB");
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(fr.error);
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(file);
      });
      const { data: saved } = await api.post(`/stores/${currentStore.id}/templates/logo`, { logo_data_url: dataUrl });
      setData(saved);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      const { data: saved } = await api.post(`/stores/${currentStore.id}/templates/logo`, { logo_data_url: "" });
      setData(saved);
      toast.success("Logo removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Favicon is a SEPARATE asset from the receipt logo. It uses the generic
  // templates PUT endpoint (no dedicated route needed) and is rendered by
  // <StoreFavicon> in the browser tab.
  const uploadFavicon = async (file) => {
    if (!file) return;
    if (file.size > 200_000) return toast.error("Favicon must be under 200 KB");
    setUploadingFav(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(fr.error);
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(file);
      });
      const { data: saved } = await api.put(`/stores/${currentStore.id}/templates`, { favicon_data_url: dataUrl });
      setData(saved);
      toast.success("Favicon uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingFav(false); if (favRef.current) favRef.current.value = ""; }
  };

  const removeFavicon = async () => {
    try {
      const { data: saved } = await api.put(`/stores/${currentStore.id}/templates`, { favicon_data_url: "" });
      setData(saved);
      toast.success("Favicon removed — using default");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-8 text-sm text-[#9CA3AF] flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading templates…</div>;

  const copyToAllStores = async () => {
    if (!stores || stores.length < 2) return;
    if (!confirm(`Copy these template settings (thermal/invoice/label/logo) to ALL ${stores.length} stores? This will overwrite other stores' templates.`)) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const s of stores) {
      if (s.id === currentStore.id) continue;
      try {
        await api.put(`/stores/${s.id}/templates`, {
          thermal: data.thermal,
          invoice: data.invoice,
          label: data.label,
          ...(data.favicon_data_url ? { favicon_data_url: data.favicon_data_url } : {}),
        });
        if (data.logo_data_url) {
          await api.post(`/stores/${s.id}/templates/logo`, { logo_data_url: data.logo_data_url });
        }
        ok++;
      } catch {
        fail++;
      }
    }
    setSaving(false);
    if (fail === 0) toast.success(`Copied to ${ok} other store${ok !== 1 ? "s" : ""}`);
    else toast.error(`Copied to ${ok}, failed on ${fail}`);
  };

  return (
    <div className="p-6 grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6 max-w-[1400px]">
      {/* LEFT — editor */}
      <div className="space-y-4">
        {/* Per-store banner — critical: user must know templates are per-store */}
        <div className="bg-[#FFF8E6] border border-[#F2D79B] rounded-xl px-4 py-3 flex items-start gap-3" data-testid="tpl-store-banner">
          <Warning size={16} weight="fill" className="text-[#B77E13] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-[#7A520B]">
              Editing templates for <span className="font-bold">{currentStore?.name || "—"}</span>
            </div>
            <div className="text-[11px] text-[#8B6310] leading-snug mt-0.5">
              Each store has its own templates. Changes here don't apply to other stores.
              {stores && stores.length > 1 && (
                <button type="button" onClick={copyToAllStores} disabled={saving || !data} data-testid="tpl-copy-all-stores" className="ml-1.5 underline font-semibold hover:text-[#7A520B] disabled:opacity-50">
                  Copy to all {stores.length} stores →
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sub-nav */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-1 flex gap-1">
          {[
            { id: "thermal", label: "Thermal", icon: Receipt },
            { id: "invoice", label: "Invoice", icon: Printer },
            { id: "label", label: "Label", icon: Tag },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setSub(t.id)}
                data-testid={`tpl-sub-${t.id}`}
                className={`flex-1 h-9 rounded-lg text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  sub === t.id ? "bg-[#121216] text-white" : "text-[#4B5563] hover:text-[#121216]"
                }`}
              >
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Logo card — shared across thermal + invoice */}
        {(sub === "thermal" || sub === "invoice") && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">Logo</div>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] flex items-center justify-center overflow-hidden shrink-0">
                {data.logo_data_url ? (
                  <img src={data.logo_data_url} alt="Store logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageSquare size={22} weight="duotone" className="text-[#9CA3AF]" />
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="tpl-logo-upload"
                  className="h-9 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] disabled:bg-[#9CA3AF] text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : "Upload logo"}
                </button>
                {data.logo_data_url && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="h-8 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#DC2626] hover:text-[#DC2626] text-[11px] font-medium flex items-center gap-1 text-[#4B5563]"
                  >
                    <Trash size={11} weight="bold" /> Remove
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              className="hidden"
            />
            <div className="text-[11px] text-[#9CA3AF] mt-2">PNG/JPG, max 500 KB. Shown on receipts & invoices.</div>
          </div>
        )}

        {/* Favicon card — separate square asset for the browser tab */}
        {(sub === "thermal" || sub === "invoice") && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Browser tab favicon</div>
              <div className="text-[10px] font-mono text-[#9CA3AF]">32×32 · square</div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] flex items-center justify-center overflow-hidden shrink-0"
                style={{ imageRendering: "-webkit-optimize-contrast" }}
              >
                {data.favicon_data_url ? (
                  <img src={data.favicon_data_url} alt="Favicon" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="w-8 h-8 rounded-md bg-[#1E5FEB] flex items-center justify-center">
                    <span className="text-white text-[9px] font-black tracking-tight">iR</span>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <button
                  type="button"
                  onClick={() => favRef.current?.click()}
                  disabled={uploadingFav}
                  data-testid="tpl-favicon-upload"
                  className="h-9 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] disabled:bg-[#9CA3AF] text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <UploadSimple size={12} weight="bold" /> {uploadingFav ? "Uploading…" : "Upload favicon"}
                </button>
                {data.favicon_data_url && (
                  <button
                    type="button"
                    onClick={removeFavicon}
                    data-testid="tpl-favicon-remove"
                    className="h-8 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#DC2626] hover:text-[#DC2626] text-[11px] font-medium flex items-center gap-1 text-[#4B5563]"
                  >
                    <Trash size={11} weight="bold" /> Use default
                  </button>
                )}
              </div>
            </div>
            <input
              ref={favRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
              onChange={(e) => uploadFavicon(e.target.files?.[0])}
              className="hidden"
            />
            <div className="text-[11px] text-[#9CA3AF] mt-2">
              PNG / SVG / ICO, square, max 200 KB. Shown in the browser tab while this store is active. Separate from the receipt logo — upload a simple, high-contrast mark.
            </div>
          </div>
        )}

        {/* Fields */}
        {sub === "thermal" && <ThermalEditor value={data.thermal} onChange={(u) => patchSection("thermal", u)} />}
        {sub === "invoice" && <InvoiceEditor value={data.invoice} onChange={(u) => patchSection("invoice", u)} />}
        {sub === "label" && <LabelEditor value={data.label} onChange={(u) => patchSection("label", u)} />}

        {/* Save */}
        <div className="sticky bottom-0 pt-2">
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="tpl-save"
            className="w-full h-12 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white font-semibold text-[13px] flex items-center justify-center gap-2 transition-colors shadow-lg shadow-[#1E5FEB]/20"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* RIGHT — Live preview */}
      <div className="space-y-3">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Live preview</div>
        <div className="rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6] p-6 min-h-[560px] flex">
          <div className="flex-1 flex items-start justify-center">
            {sub === "thermal" && <ThermalPreview data={data} />}
            {sub === "invoice" && <InvoicePreview data={data} />}
            {sub === "label" && <LabelPreview data={data} />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ EDITORS ============ */
function ThermalEditor({ value, onChange }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-5">
      <Group title="Output format (how receipts are sent to the printer)">
        <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3">
          <label className="flex items-start gap-3 mb-2 cursor-pointer">
            <input
              type="radio"
              name="thermal_output"
              value="escpos"
              checked={(value.thermal_output || "escpos") === "escpos"}
              onChange={() => onChange({ thermal_output: "escpos" })}
              data-testid="t-output-escpos"
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#121216] flex items-center gap-2">
                ESC/POS (raw)
                <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#2F8F6E]">RECOMMENDED</span>
              </div>
              <div className="text-[11px] text-[#6B7280] leading-relaxed mt-0.5">
                Sends raw commands straight to the printer — no driver padding, no blank-paper strip.
                Same approach RepairDesk / Loyverse use. Works with Epson TM-T20/T82/T88 and most 80mm thermals.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="thermal_output"
              value="pdf"
              checked={value.thermal_output === "pdf"}
              onChange={() => onChange({ thermal_output: "pdf" })}
              data-testid="t-output-pdf"
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#121216]">PDF</div>
              <div className="text-[11px] text-[#6B7280] leading-relaxed mt-0.5">
                Lets the Windows printer driver render a PDF. Richer layout (logo, fonts) but may add
                blank paper depending on driver settings.
              </div>
            </div>
          </label>
        </div>
      </Group>
      <Group title="Header">
        <Text label="Business name" value={value.header_name} onChange={(v) => onChange({ header_name: v })} testid="t-header-name" />
        <Text label="Address" value={value.header_address} onChange={(v) => onChange({ header_address: v })} testid="t-header-address" />
        <Text label="Phone" value={value.header_phone} onChange={(v) => onChange({ header_phone: v })} testid="t-header-phone" mono />
      </Group>
      <Group title="Footer">
        <Text label="Thank-you line" value={value.footer_thanks} onChange={(v) => onChange({ footer_thanks: v })} testid="t-footer-thanks" />
        <Text label="Return policy" value={value.footer_return_policy} onChange={(v) => onChange({ footer_return_policy: v })} testid="t-footer-policy" />
        <Text label="Website" value={value.footer_website} onChange={(v) => onChange({ footer_website: v })} testid="t-footer-website" />
        <Text label="Custom message" value={value.custom_message} onChange={(v) => onChange({ custom_message: v })} testid="t-custom-message" placeholder="Follow us @irepairbrisbane — 5★ on Google!" />
      </Group>
      <Group title="Warranty & terms (printed under Terms & Conditions)">
        <p className="text-[11px] text-[#6B7280] leading-relaxed mb-1">
          Each block prints only when the matching line type appears on the receipt —
          Service = repair tickets, Inventory = products.
        </p>
        <TextArea label="Service warranty (repair items)" value={value.service_warranty_terms} onChange={(v) => onChange({ service_warranty_terms: v })} testid="t-service-warranty" placeholder="Repair warranty covers defects in workmanship or replacement parts…" />
        <TextArea label="Inventory warranty (products & accessories)" value={value.inventory_warranty_terms} onChange={(v) => onChange({ inventory_warranty_terms: v })} testid="t-inventory-warranty" placeholder="Accessories sold as new carry a 30-day defect warranty…" />
        <TextArea label="General warranty footer" value={value.footer_warranty_terms} onChange={(v) => onChange({ footer_warranty_terms: v })} testid="t-footer-warranty-terms" placeholder="Bring this receipt for any warranty claim." />
      </Group>
      <Group title="Per-item warranty line (the short “Warranty: …” under each item)">
        <p className="text-[11px] text-[#6B7280] leading-relaxed mb-1">
          Controls the default text shown next to each line item. Repair tickets with an explicit warranty will still use that number of months.
        </p>
        <Text label="Repair items default" value={value.service_item_warranty_default} onChange={(v) => onChange({ service_item_warranty_default: v })} testid="t-service-item-warranty" placeholder="e.g. 3 months · parts & labour" />
        <Text label="Inventory items default" value={value.inventory_item_warranty_default} onChange={(v) => onChange({ inventory_item_warranty_default: v })} testid="t-inventory-item-warranty" placeholder="e.g. 30-day defect · no accidental damage" />
        <Toggle label="Show per-item warranty line" value={value.show_per_item_warranty ?? true} onChange={(v) => onChange({ show_per_item_warranty: v })} testid="t-show-per-item-warranty" />
      </Group>
      <Group title="Show on receipt">
        <Toggle label="Customer name" value={value.show_customer} onChange={(v) => onChange({ show_customer: v })} testid="t-show-customer" />
        <Toggle label="Cashier name" value={value.show_cashier} onChange={(v) => onChange({ show_cashier: v })} testid="t-show-cashier" />
        <Toggle label="Tax line" value={value.show_tax} onChange={(v) => onChange({ show_tax: v })} testid="t-show-tax" />
        <Toggle label="Sale notes" value={value.show_notes} onChange={(v) => onChange({ show_notes: v })} testid="t-show-notes" />
      </Group>
    </div>
  );
}

function InvoiceEditor({ value, onChange }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-5">
      <Group title="Business details">
        <Text label="Business name" value={value.business_name} onChange={(v) => onChange({ business_name: v })} testid="i-name" />
        <Text label="ABN" value={value.abn} onChange={(v) => onChange({ abn: v })} testid="i-abn" mono placeholder="12 345 678 901" />
        <TextArea label="Address" value={value.address} onChange={(v) => onChange({ address: v })} testid="i-address" />
        <Text label="Phone" value={value.phone} onChange={(v) => onChange({ phone: v })} testid="i-phone" mono />
        <Text label="Email" value={value.email} onChange={(v) => onChange({ email: v })} testid="i-email" />
      </Group>
      <Group title="Colors">
        <div className="grid grid-cols-2 gap-2">
          <ColorPicker label="Header color" value={value.header_color} onChange={(v) => onChange({ header_color: v })} testid="i-header-color" />
          <ColorPicker label="Accent color" value={value.accent_color} onChange={(v) => onChange({ accent_color: v })} testid="i-accent-color" />
        </div>
      </Group>
      <Group title="Terms & footer">
        <TextArea label="Payment terms" value={value.payment_terms} onChange={(v) => onChange({ payment_terms: v })} testid="i-terms" placeholder="Payment due within 7 days." />
        <TextArea label="Bank details" value={value.bank_details} onChange={(v) => onChange({ bank_details: v })} testid="i-bank" placeholder="BSB: 123-456&#10;Acc: 1234 5678&#10;Ref: <invoice #>" />
        <TextArea label="Footer small-print" value={value.footer_small_print} onChange={(v) => onChange({ footer_small_print: v })} testid="i-footer" />
      </Group>
    </div>
  );
}

function LabelEditor({ value, onChange }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-5">
      <Group title="Fields to include">
        <Toggle label="Ticket number" value={value.show_ticket_number} onChange={(v) => onChange({ show_ticket_number: v })} testid="l-show-ticket" />
        <Toggle label="Customer name" value={value.show_customer_name} onChange={(v) => onChange({ show_customer_name: v })} testid="l-show-customer" />
        <Toggle label="Phone" value={value.show_phone} onChange={(v) => onChange({ show_phone: v })} testid="l-show-phone" />
        <Toggle label="Device make/model" value={value.show_device} onChange={(v) => onChange({ show_device: v })} testid="l-show-device" />
        <Toggle label="IMEI" value={value.show_imei} onChange={(v) => onChange({ show_imei: v })} testid="l-show-imei" />
        <Toggle label="Issue" value={value.show_issue} onChange={(v) => onChange({ show_issue: v })} testid="l-show-issue" />
        <Toggle label="Estimated cost" value={value.show_estimated_cost} onChange={(v) => onChange({ show_estimated_cost: v })} testid="l-show-cost" />
        <Toggle label="Intake date" value={value.show_intake_date} onChange={(v) => onChange({ show_intake_date: v })} testid="l-show-date" />
      </Group>
      <Group title="Extra text">
        <Text label="Top line" value={value.extra_top} onChange={(v) => onChange({ extra_top: v })} testid="l-extra-top" placeholder="HANDLE WITH CARE" />
        <Text label="Bottom line" value={value.extra_bottom} onChange={(v) => onChange({ extra_bottom: v })} testid="l-extra-bottom" />
      </Group>
    </div>
  );
}

/* ============ PREVIEWS ============ */
const MOCK_SALE = {
  sale_number: "S-000123",
  date: "2026-02-15 14:32",
  cashier_name: "Jake (Cashier)",
  customer_name: "Jane Doe",
  items: [
    { name: "Screen repair — iPhone 14 Pro", qty: 1, unit: 249, total: 249, kind: "repair", ref: "R-00045" },
    { name: "Tempered glass screen protector", qty: 2, unit: 14.99, total: 29.98 },
    { name: "USB-C charging cable 1m", qty: 1, unit: 12.50, total: 12.50 },
  ],
  subtotal: 291.48,
  discount: 10,
  tax: 28.15,
  total: 309.63,
  tendered: 320,
  change: 10.37,
  note: "Glass replacement only, frame kept",
};

function ThermalPreview({ data }) {
  const t = data.thermal;
  return (
    <div
      className="mx-auto bg-white shadow-lg rounded-sm px-6 py-5 max-w-[340px] text-black text-[11px] leading-[1.55]"
      style={{ fontFamily: "Helvetica, Arial, sans-serif" }}
      data-testid="tpl-thermal-preview"
    >
      {/* HERO — Invoice# */}
      <div className="text-center font-bold text-[17px] tracking-tight">Invoice# {MOCK_SALE.sale_number}</div>

      {/* LOGO */}
      {data.logo_data_url && (
        <div className="flex justify-center mt-2 mb-1">
          <img src={data.logo_data_url} alt="" className="max-h-12 object-contain" />
        </div>
      )}

      {/* STORE BLOCK */}
      <div className="mt-2 space-y-0.5">
        <div className="font-bold text-[12px] tracking-tight">{t.header_name || "Your business name"}</div>
        {t.header_address && <div>{t.header_address}</div>}
        {t.header_phone && <div>Phone: {t.header_phone}</div>}
        {t.footer_website && <div>Web: {t.footer_website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</div>}
        <div>Date: {MOCK_SALE.date}</div>
      </div>
      <DashLine />

      {/* CUSTOMER BLOCK */}
      <div className="font-bold text-[14px] tracking-tight">{MOCK_SALE.customer_name}</div>
      <div className="mb-1">Mobile: +61 400 123 456</div>

      {/* TICKET ROW */}
      <div><span className="font-bold">Ticket:</span> R-00045</div>
      {t.show_cashier && <div><span className="font-bold">Prepared By:</span> {MOCK_SALE.cashier_name}</div>}
      <DashLine />

      {/* ITEM TABLE */}
      <div className="grid grid-cols-[1fr_32px_64px] gap-2 font-bold">
        <span>Item Name</span>
        <span>QTY</span>
        <span className="text-right">Price</span>
      </div>
      <DashLine />
      {MOCK_SALE.items.map((it, i) => (
        <div key={i} className="mb-1.5">
          <div className="grid grid-cols-[1fr_32px_64px] gap-2">
            <span className="font-bold">{it.name}</span>
            <span>{it.qty}</span>
            <span className="text-right font-bold">${it.total.toFixed(2)}</span>
          </div>
          {it.kind === "repair" ? (
            <>
              <div className="text-[9.5px] text-zinc-600">Manufacturer: Apple</div>
              <div className="text-[9.5px] text-zinc-600">Device: iPhone 14 Pro</div>
              <div className="text-[9.5px] text-zinc-600">Warranty: 3 months</div>
            </>
          ) : (
            <div className="text-[9.5px] text-zinc-600">Warranty: No Warranty</div>
          )}
        </div>
      ))}
      <DashLine />

      {/* TOTALS */}
      <div className="flex justify-between font-bold"><span>Sub Total</span><span>${MOCK_SALE.subtotal.toFixed(2)}</span></div>
      <div className="flex justify-between font-bold"><span>Discount</span><span>-${MOCK_SALE.discount.toFixed(2)}</span></div>
      {t.show_tax && <div className="flex justify-between font-bold"><span>GST</span><span>${MOCK_SALE.tax.toFixed(2)}</span></div>}
      <div className="flex justify-between font-bold text-[14px] mt-0.5"><span>Total</span><span>${MOCK_SALE.total.toFixed(2)}</span></div>
      <div className="flex justify-between font-bold mt-1"><span>Paid</span><span>${MOCK_SALE.tendered.toFixed(2)}</span></div>
      <div className="text-[9.5px] text-zinc-700">Cash {MOCK_SALE.date}  ${MOCK_SALE.tendered.toFixed(2)}</div>
      <div className="flex justify-between font-bold"><span>Balance</span><span>$0.00</span></div>

      {t.show_notes && MOCK_SALE.note && (
        <>
          <DashLine />
          <div className="text-[10px] italic">Note: {MOCK_SALE.note}</div>
        </>
      )}
      <DashLine />

      {/* TERMS & CONDITIONS */}
      <div className="font-bold">Terms & Conditions</div>
      {t.service_warranty_terms && (
        <div className="mt-0.5">
          <div className="text-[9px] font-bold tracking-wider">REPAIR WARRANTY</div>
          <div className="text-[9.5px] leading-snug text-zinc-700">{t.service_warranty_terms}</div>
        </div>
      )}
      {t.inventory_warranty_terms && (
        <div className="mt-1">
          <div className="text-[9px] font-bold tracking-wider">PRODUCT WARRANTY</div>
          <div className="text-[9.5px] leading-snug text-zinc-700">{t.inventory_warranty_terms}</div>
        </div>
      )}
      {t.footer_warranty_terms && (
        <div className="mt-1">
          <div className="text-[9px] font-bold tracking-wider">GENERAL</div>
          <div className="text-[9.5px] leading-snug text-zinc-700">{t.footer_warranty_terms}</div>
        </div>
      )}

      <div className="text-center mt-2">
        <div className="font-bold">{t.footer_thanks || "Thank you!"}</div>
        {t.footer_return_policy && <div className="text-[9px]">{t.footer_return_policy}</div>}
        {t.custom_message && <div className="text-[9px] italic mt-1">{t.custom_message}</div>}
      </div>
    </div>
  );
}

function InvoicePreview({ data }) {
  const i = data.invoice;
  return (
    <div className="mx-auto bg-white shadow-xl rounded-sm overflow-hidden max-w-[680px] text-black text-[11px]" data-testid="tpl-invoice-preview">
      {/* Header */}
      <div style={{ background: i.header_color || "#121216", color: "white" }} className="px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data.logo_data_url && <img src={data.logo_data_url} alt="" className="w-12 h-12 object-contain bg-white rounded-md p-1" />}
          <div className="opacity-80 text-[10px] tracking-[0.2em] uppercase">Invoice</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-[22px] tracking-tight">INVOICE</div>
          <div className="text-[11px] mt-1 font-mono">{MOCK_SALE.sale_number}</div>
          <div className="text-[10px] opacity-70 mt-0.5">{MOCK_SALE.date}</div>
        </div>
      </div>
      {/* Bodies */}
      <div className="px-8 py-6 grid grid-cols-2 gap-6">
        <div>
          <div className="font-bold text-[13px]">{i.business_name || "Your business name"}</div>
          <div className="text-[10px] text-[#4B5563] space-y-0.5 mt-1">
            {i.abn && <div>ABN {i.abn}</div>}
            {i.address && i.address.split("\n").map((l, k) => <div key={k}>{l}</div>)}
            {(i.phone || i.email) && <div>{[i.phone, i.email].filter(Boolean).join(" · ")}</div>}
          </div>
        </div>
        <div>
          <div className="text-[9px] tracking-[0.2em] uppercase text-[#9CA3AF]">Billed to</div>
          <div className="font-bold text-[13px] mt-1">{MOCK_SALE.customer_name}</div>
          <div className="text-[10px] text-[#4B5563] mt-1">Payment · Cash</div>
        </div>
      </div>
      {/* Table */}
      <div className="px-8">
        <div style={{ background: i.accent_color || "#1E5FEB" }} className="text-white text-[9px] tracking-[0.2em] uppercase px-3 py-2 grid grid-cols-[1fr_60px_70px_80px] gap-3">
          <div>Description</div><div>Qty</div><div>Unit</div><div className="text-right">Amount</div>
        </div>
        {MOCK_SALE.items.map((it, k) => (
          <div key={k} className="px-3 py-3 border-b border-[#F3F4F6] grid grid-cols-[1fr_60px_70px_80px] gap-3 items-start">
            <div>
              <div className="font-bold">{it.name}</div>
              {it.ref && <div className="text-[9px] text-[#9CA3AF]">Ticket {it.ref}</div>}
            </div>
            <div>{it.qty}</div>
            <div>${it.unit.toFixed(2)}</div>
            <div className="text-right">${it.total.toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="px-8 py-4 flex justify-end">
        <div className="w-64 space-y-1 text-[11px]">
          <Row k="Subtotal" v={MOCK_SALE.subtotal} />
          <Row k="Discount" v={-MOCK_SALE.discount} neg />
          <Row k="Tax" v={MOCK_SALE.tax} />
          <div className="flex justify-between font-bold text-[15px] pt-2 border-t border-[#E5E7EB]" style={{ color: i.accent_color || "#1E5FEB" }}>
            <span>TOTAL</span><span>${MOCK_SALE.total.toFixed(2)}</span>
          </div>
        </div>
      </div>
      {(i.payment_terms || i.bank_details) && (
        <div className="px-8 pb-6 grid grid-cols-2 gap-6">
          {i.payment_terms && <div><div className="font-bold text-[11px] mb-1">Payment terms</div><div className="text-[10px] text-[#4B5563] whitespace-pre-line">{i.payment_terms}</div></div>}
          {i.bank_details && <div><div className="font-bold text-[11px] mb-1">Bank details</div><div className="text-[10px] text-[#4B5563] whitespace-pre-line font-mono">{i.bank_details}</div></div>}
        </div>
      )}
      {i.footer_small_print && (
        <div className="px-8 pb-4 text-center text-[9px] text-[#9CA3AF] border-t border-[#F3F4F6] pt-3">{i.footer_small_print}</div>
      )}
    </div>
  );
}

function LabelPreview({ data }) {
  const l = data.label;
  const sample = {
    ticket_number: "R-00045", customer_name: "Jane Doe", customer_phone: "+61 400 000 000",
    device_make: "Apple", device_model: "iPhone 14 Pro", imei: "359xxxxxxxxxx45", issue: "Cracked screen + Face ID fault", estimated_cost: 249,
    intake_date: "15 FEB 26", intake_time: "14:32",
  };
  const paymentBadge = { label: "PAID IN FULL", bg: "#1F9D55" };
  const bizName = (data.invoice?.business_name || data.thermal?.header_name || "REPAIR SHOP").toUpperCase();
  const phone = data.invoice?.phone || data.thermal?.header_phone || "";
  return (
    <div className="w-full h-full flex flex-col items-center justify-center" data-testid="tpl-label-preview">
      <div className="text-[10px] text-[#9CA3AF] mb-3 font-mono tracking-wider">DYMO 1.125" × 3.5" — actual print scale</div>
      <div
        className="bg-white relative text-black flex overflow-hidden"
        style={{
          width: 378,
          height: 121,
          fontFamily: "Helvetica, Arial, sans-serif",
          boxShadow: "0 12px 28px rgba(30, 95, 235, 0.14), 0 2px 4px rgba(17, 17, 17, 0.06)",
          border: "1px solid #E5E7EB",
          borderRadius: 4,
        }}
      >
        {/* Extra top strip */}
        {l.extra_top && (
          <div className="absolute top-0 left-0 right-0 bg-[#121216] text-white text-[7px] font-bold tracking-[0.2em] uppercase text-center py-[2px] z-10">
            {l.extra_top}
          </div>
        )}

        {/* LEFT HERO COLUMN — ticket + intake date on tinted background */}
        <div
          className="flex flex-col items-center justify-center relative"
          style={{
            width: 110,
            paddingTop: l.extra_top ? 14 : 6,
            paddingBottom: l.extra_bottom ? 14 : 6,
            background: "#F6F7FA",
          }}
        >
          {/* Dashed divider simulating PDF */}
          <div
            className="absolute top-2 bottom-2 right-0"
            style={{
              width: 1,
              backgroundImage: "linear-gradient(to bottom, #C8CCD3 50%, transparent 50%)",
              backgroundSize: "1px 4px",
              backgroundRepeat: "repeat-y",
            }}
          />
          {l.show_ticket_number && (
            <>
              <div className="text-[7px] tracking-[0.28em] uppercase text-[#6B7280] font-bold">Ticket</div>
              <div className="font-bold tracking-tight text-[#121216]" style={{ fontSize: 22, lineHeight: 1.1 }}>
                {sample.ticket_number}
              </div>
            </>
          )}
          {l.show_intake_date && (
            <div className="mt-2 flex flex-col items-center" style={{ minWidth: 70 }}>
              <div className="h-px w-10 bg-[#C8CCD3]" />
              <div className="text-[9px] font-mono font-bold text-[#121216] mt-1 tracking-wider">{sample.intake_date}</div>
              <div className="text-[8px] font-mono text-[#6B7280]">{sample.intake_time}</div>
            </div>
          )}
        </div>

        {/* RIGHT CONTENT COLUMN */}
        <div
          className="flex-1 flex flex-col min-w-0 px-3"
          style={{
            paddingTop: l.extra_top ? 14 : 6,
            paddingBottom: l.extra_bottom ? 14 : 6,
          }}
        >
          {/* Header row: business name · phone */}
          <div className="flex items-baseline justify-between gap-2 border-b border-[#E5E7EB] pb-1">
            <div className="font-bold text-[8px] tracking-[0.12em] text-[#121216] truncate">{bizName}</div>
            {phone && <div className="text-[7px] font-mono font-bold text-[#6B7280] whitespace-nowrap">{phone}</div>}
          </div>

          {/* Stack */}
          <div className="flex-1 flex flex-col justify-start pt-1 min-w-0 space-y-[2px]">
            {l.show_customer_name && (
              <div className="font-bold text-[12px] leading-tight text-[#121216] truncate">{sample.customer_name}</div>
            )}
            {l.show_phone && (
              <div className="text-[9px] font-mono font-bold text-[#121216] truncate">{sample.customer_phone}</div>
            )}
            {l.show_device && (
              <div className="text-[9px] font-bold text-[#121216] truncate">
                {sample.device_make} {sample.device_model}
              </div>
            )}
            {l.show_imei && (
              <div className="text-[7.5px] font-mono text-[#6B7280] truncate">IMEI {sample.imei}</div>
            )}
            {l.show_issue && (
              <div className="text-[8px] text-[#4B5563] truncate italic">{sample.issue}</div>
            )}
          </div>

          {/* EST + payment badge pinned together to bottom */}
          {l.show_estimated_cost && (
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[11px] font-bold text-[#121216] tabular-nums whitespace-nowrap" data-testid="tpl-label-estimated-cost">
                EST ${sample.estimated_cost.toFixed(2)}
              </div>
              <div
                className="text-white text-[7px] font-bold px-2 py-[2px] rounded-full whitespace-nowrap leading-none"
                style={{ background: paymentBadge.bg }}
                data-testid="tpl-label-payment-badge"
              >
                {paymentBadge.label}
              </div>
            </div>
          )}
        </div>

        {/* Extra bottom strip */}
        {l.extra_bottom && (
          <div className="absolute bottom-0 left-0 right-0 bg-[#121216] text-white text-[7px] font-bold tracking-[0.2em] uppercase text-center py-[2px] z-10">
            {l.extra_bottom}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ PRIMITIVES ============ */
function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.14em] uppercase text-[#9CA3AF] font-semibold mb-2">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function Text({ label, value, onChange, mono, placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-[#4B5563] mb-1">{label}</div>
      <input
        type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""}
        data-testid={testid}
        className={`w-full h-9 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] text-[13px] bg-white transition-all ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}
function TextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-[#4B5563] mb-1">{label}</div>
      <textarea
        rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] text-[13px] bg-white transition-all resize-y"
      />
    </label>
  );
}
function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
      <span className="text-[12px] text-[#121216]">{label}</span>
      <button
        type="button" role="switch" aria-checked={!!value}
        data-testid={testid}
        onClick={() => onChange(!value)}
        className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${value ? "bg-[#1E5FEB]" : "bg-[#E5E7EB]"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${value ? "translate-x-4" : ""}`} />
      </button>
    </label>
  );
}
function ColorPicker({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-[#4B5563] mb-1">{label}</div>
      <div className="h-9 px-2 rounded-lg border border-[#E5E7EB] bg-white flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-6 h-6 rounded cursor-pointer border-0 p-0" />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 text-[12px] font-mono bg-transparent focus:outline-none" />
      </div>
    </label>
  );
}
function DashLine() {
  return <div className="my-1 border-t border-dashed border-black/60" />;
}
function Row({ k, v, neg }) {
  const label = v < 0 ? `-$${Math.abs(v).toFixed(2)}` : `$${Number(v).toFixed(2)}`;
  return (
    <div className="flex justify-between">
      <span>{k}</span>
      <span>{neg ? label : `$${Number(v).toFixed(2)}`}</span>
    </div>
  );
}
